/**
 * Fase 6 — métricas e relatórios: definições das métricas, cálculo das derivadas (CTR, CPC,
 * CPM, CPA, ROAS, frequência), agregação ponderada (nunca média de médias), comparação de
 * períodos, mapeamento de "resultados" por objetivo/otimização e utilidades de fuso.
 * Funções puras: valem na API, na interface e nos testes.
 */
import { presetToRange, type DateRange, type PeriodPreset } from "./campaign-enums.js";

export const INSIGHT_LEVELS = ["account", "campaign", "adset", "ad"] as const;
export type InsightLevel = (typeof INSIGHT_LEVELS)[number];

export const BREAKDOWN_KINDS = ["age", "gender", "placement", "region", "device"] as const;
export type BreakdownKind = (typeof BREAKDOWN_KINDS)[number];

export const BREAKDOWN_LABELS: Record<BreakdownKind, string> = {
  age: "Idade",
  gender: "Gênero",
  placement: "Posicionamento",
  region: "Região",
  device: "Dispositivo",
};

/** Somas brutas de um conjunto de linhas de insights (centavos nos valores monetários). */
export interface MetricSums {
  spendCents: number;
  impressions: number;
  reach: number;
  clicks: number;
  linkClicks: number;
  results: number;
  purchases: number;
  purchaseValueCents: number;
  leads: number;
  registrations: number;
  messages: number;
  thruplays: number;
  videoP25: number;
  videoP50: number;
  videoP75: number;
  videoP100: number;
}

export const EMPTY_SUMS: MetricSums = {
  spendCents: 0,
  impressions: 0,
  reach: 0,
  clicks: 0,
  linkClicks: 0,
  results: 0,
  purchases: 0,
  purchaseValueCents: 0,
  leads: 0,
  registrations: 0,
  messages: 0,
  thruplays: 0,
  videoP25: 0,
  videoP50: 0,
  videoP75: 0,
  videoP100: 0,
};

/** Somas + derivadas (as derivadas sempre saem das somas: média ponderada por construção). */
export interface Metrics extends MetricSums {
  /** impressões / alcance */
  frequency: number | null;
  /** cliques / impressões, em % (0–100) */
  ctr: number | null;
  /** cliques no link / impressões, em % */
  linkCtr: number | null;
  cpcCents: number | null;
  cpmCents: number | null;
  costPerResultCents: number | null;
  costPerLeadCents: number | null;
  costPerPurchaseCents: number | null;
  /** valor de compra / gasto */
  roas: number | null;
}

function ratio(num: number, den: number): number | null {
  return den > 0 ? num / den : null;
}

/** Derivadas a partir das somas. Nunca somar/mediar CTR, CPC, etc. de linhas: some e derive. */
export function deriveMetrics(sums: MetricSums): Metrics {
  const cpc = ratio(sums.spendCents, sums.clicks);
  const cpm = ratio(sums.spendCents * 1000, sums.impressions);
  return {
    ...sums,
    frequency: ratio(sums.impressions, sums.reach),
    ctr: sums.impressions > 0 ? (sums.clicks / sums.impressions) * 100 : null,
    linkCtr: sums.impressions > 0 ? (sums.linkClicks / sums.impressions) * 100 : null,
    cpcCents: cpc === null ? null : Math.round(cpc),
    cpmCents: cpm === null ? null : Math.round(cpm),
    costPerResultCents: sums.results > 0 ? Math.round(sums.spendCents / sums.results) : null,
    costPerLeadCents: sums.leads > 0 ? Math.round(sums.spendCents / sums.leads) : null,
    costPerPurchaseCents: sums.purchases > 0 ? Math.round(sums.spendCents / sums.purchases) : null,
    roas: sums.spendCents > 0 ? sums.purchaseValueCents / sums.spendCents : null,
  };
}

export const SUM_KEYS = Object.keys(EMPTY_SUMS) as (keyof MetricSums)[];

/** Soma linhas (ponderação natural: as derivadas saem do total). */
export function sumMetrics(rows: Iterable<Partial<MetricSums>>): MetricSums {
  const out: MetricSums = { ...EMPTY_SUMS };
  for (const r of rows) for (const k of SUM_KEYS) out[k] += r[k] ?? 0;
  return out;
}

export function aggregateMetrics(rows: Iterable<Partial<MetricSums>>): Metrics {
  return deriveMetrics(sumMetrics(rows));
}

// ---- Comparação de períodos ---------------------------------------------------------------------

export interface MetricDelta {
  current: number | null;
  previous: number | null;
  /** Variação relativa em % (null quando não dá para comparar). */
  deltaPct: number | null;
  /** `melhor` / `pior` / `igual` já considerando se a métrica é de custo (menor é melhor). */
  direction: "melhor" | "pior" | "igual" | null;
}

/** Métricas em que menor é melhor. */
export const LOWER_IS_BETTER: readonly (keyof Metrics)[] = [
  "cpcCents",
  "cpmCents",
  "costPerResultCents",
  "costPerLeadCents",
  "costPerPurchaseCents",
  "frequency",
];

export function compareMetric(
  key: keyof Metrics,
  current: number | null,
  previous: number | null,
): MetricDelta {
  if (current === null || previous === null || previous === 0) {
    return { current, previous, deltaPct: null, direction: null };
  }
  const deltaPct = ((current - previous) / Math.abs(previous)) * 100;
  const rounded = Math.round(deltaPct * 10) / 10;
  let direction: MetricDelta["direction"] = "igual";
  if (Math.abs(rounded) >= 0.05) {
    const lower = LOWER_IS_BETTER.includes(key);
    direction = rounded > 0 !== lower ? "melhor" : "pior";
  }
  return { current, previous, deltaPct: rounded, direction };
}

/** Período imediatamente anterior, com o mesmo número de dias. */
export function previousRange(range: { from: string; to: string }): { from: string; to: string } {
  const from = parseIsoDate(range.from);
  const to = parseIsoDate(range.to);
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  return { from: isoDate(addDays(from, -days)), to: isoDate(addDays(to, -days)) };
}

export function parseIsoDate(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

export function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDays(d: Date, days: number): Date {
  return new Date(d.getTime() + days * 86_400_000);
}

/** Lista de dias `AAAA-MM-DD` (inclusivo). */
export function eachDay(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = parseIsoDate(from); d.getTime() <= parseIsoDate(to).getTime(); d = addDays(d, 1))
    out.push(isoDate(d));
  return out;
}

export function daysBetween(from: string, to: string): number {
  return Math.round((parseIsoDate(to).getTime() - parseIsoDate(from).getTime()) / 86_400_000) + 1;
}

// ---- Fuso da conta -------------------------------------------------------------------------------

/** Data `AAAA-MM-DD` no fuso informado (a Meta reporta insights no fuso da conta de anúncio). */
export function dateInTimeZone(instant: Date, timeZone: string | null | undefined): string {
  try {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone ?? "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return fmt.format(instant);
  } catch {
    return isoDate(instant);
  }
}

/** Resolve o período (preset ou intervalo) numa faixa fechada, no fuso da conta. */
export function resolvePeriod(
  input: { preset?: PeriodPreset | undefined; from?: string | undefined; to?: string | undefined },
  options: { timeZone?: string | null; now?: Date; maxDays?: number } = {},
): { from: string; to: string } {
  const today = dateInTimeZone(options.now ?? new Date(), options.timeZone);
  const maxDays = options.maxDays ?? 90;
  let range: DateRange;
  if (input.preset) {
    range = presetToRange(input.preset, parseIsoDateLocal(today));
  } else {
    range = { from: input.from ?? null, to: input.to ?? null };
  }
  const to = range.to ?? today;
  const from = range.from ?? isoDate(addDays(parseIsoDate(to), -(maxDays - 1)));
  return from <= to ? { from, to } : { from: to, to: from };
}

/** `Date` local (sem fuso) para `presetToRange`, que calcula com getters locais. */
function parseIsoDateLocal(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

// ---- "Resultados" por objetivo / otimização ---------------------------------------------------

export type ResultType =
  | "compras"
  | "cadastros"
  | "cliques_no_link"
  | "visualizacoes_pagina"
  | "conversas"
  | "thruplays"
  | "engajamentos"
  | "alcance"
  | "instalacoes"
  | "registros"
  | "impressoes"
  | "misto";

export const RESULT_TYPE_LABELS: Record<ResultType, string> = {
  compras: "Compras",
  cadastros: "Cadastros",
  cliques_no_link: "Cliques no link",
  visualizacoes_pagina: "Visualizações da página",
  conversas: "Conversas iniciadas",
  thruplays: "ThruPlays",
  engajamentos: "Engajamentos",
  alcance: "Alcance",
  instalacoes: "Instalações",
  registros: "Registros",
  impressoes: "Impressões",
  misto: "Resultados",
};

/** Tipos de ação da Meta (`actions[].action_type`) que contam para cada resultado. */
export const RESULT_ACTION_TYPES: Record<
  Exclude<ResultType, "alcance" | "impressoes" | "misto">,
  string[]
> = {
  compras: ["omni_purchase", "purchase", "offsite_conversion.fb_pixel_purchase"],
  cadastros: ["lead", "onsite_conversion.lead_grouped", "offsite_conversion.fb_pixel_lead"],
  cliques_no_link: ["link_click"],
  visualizacoes_pagina: ["landing_page_view"],
  conversas: ["onsite_conversion.messaging_conversation_started_7d"],
  thruplays: ["video_thruplay_watched"],
  engajamentos: ["post_engagement"],
  instalacoes: ["omni_app_install", "mobile_app_install"],
  registros: ["omni_complete_registration", "complete_registration"],
};

/**
 * Decide o que é "resultado" de um item a partir da otimização do conjunto (preferida) ou do
 * objetivo da campanha, como o Gerenciador faz.
 */
export function resultTypeFor(input: {
  optimizationGoal?: string | null;
  objective?: string | null;
}): ResultType {
  switch (input.optimizationGoal) {
    case "OFFSITE_CONVERSIONS":
    case "VALUE":
      return "compras";
    case "LEAD_GENERATION":
    case "QUALITY_LEAD":
      return "cadastros";
    case "LINK_CLICKS":
      return "cliques_no_link";
    case "LANDING_PAGE_VIEWS":
      return "visualizacoes_pagina";
    case "CONVERSATIONS":
      return "conversas";
    case "THRUPLAY":
      return "thruplays";
    case "POST_ENGAGEMENT":
    case "PAGE_LIKES":
      return "engajamentos";
    case "REACH":
      return "alcance";
    case "IMPRESSIONS":
      return "impressoes";
    case "APP_INSTALLS":
      return "instalacoes";
    default:
      break;
  }
  switch (input.objective) {
    case "OUTCOME_SALES":
    case "CONVERSIONS":
    case "PRODUCT_CATALOG_SALES":
      return "compras";
    case "OUTCOME_LEADS":
    case "LEAD_GENERATION":
      return "cadastros";
    case "OUTCOME_TRAFFIC":
    case "LINK_CLICKS":
      return "cliques_no_link";
    case "OUTCOME_ENGAGEMENT":
    case "POST_ENGAGEMENT":
    case "VIDEO_VIEWS":
      return "engajamentos";
    case "MESSAGES":
      return "conversas";
    case "OUTCOME_AWARENESS":
    case "REACH":
    case "BRAND_AWARENESS":
      return "alcance";
    case "OUTCOME_APP_PROMOTION":
    case "APP_INSTALLS":
      return "instalacoes";
    default:
      return "misto";
  }
}

export interface ActionEntry {
  action_type: string;
  value: string | number;
}

/** Soma o valor de um conjunto de tipos de ação (primeiro tipo encontrado vence, sem dupla contagem). */
export function actionValue(actions: ActionEntry[] | null | undefined, types: string[]): number {
  if (!actions) return 0;
  for (const t of types) {
    const hit = actions.find((a) => a.action_type === t);
    if (hit) return Number(hit.value) || 0;
  }
  return 0;
}

/** Quantidade de "resultados" numa linha de insights, dado o tipo de resultado. */
export function resultsOf(
  type: ResultType,
  row: {
    actions?: ActionEntry[] | null;
    reach?: number;
    impressions?: number;
    thruplays?: number;
  },
): number {
  if (type === "alcance") return row.reach ?? 0;
  if (type === "impressoes") return row.impressions ?? 0;
  if (type === "misto") return 0;
  if (type === "thruplays" && row.thruplays !== undefined) return row.thruplays;
  return actionValue(row.actions, RESULT_ACTION_TYPES[type]);
}

export * from "./metric-defs.js";

import { METRIC_DEFS, type MetricDef } from "./metric-defs.js";

/** Formata uma métrica para exibição em pt-BR (interface, PDF e exportação legível). */
export function formatMetric(
  def: Pick<MetricDef, "format">,
  value: number | null | undefined,
  currency: string,
): string {
  if (value === null || value === undefined) return "—";
  switch (def.format) {
    case "money":
      try {
        return new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(value / 100);
      } catch {
        return `${(value / 100).toFixed(2)} ${currency}`;
      }
    case "int":
      return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);
    case "pct":
      return `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(value)}%`;
    case "ratio":
      return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(value);
    case "roas":
      return `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(value)}x`;
  }
}

export const METRIC_LABELS = Object.fromEntries(METRIC_DEFS.map((m) => [m.id, m.label]));

/** Métricas dos cartões de KPI do dashboard (ordem de exibição). */
export const KPI_METRICS: readonly (keyof Metrics)[] = [
  "spendCents",
  "results",
  "costPerResultCents",
  "ctr",
  "roas",
  "leads",
];
